import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useLocation } from 'react-router-dom';
import PropTypes from "prop-types";
import InactivityWarning from "../components/inactivity-warning/inactivity-warning";

let logoutTimer;
let timeoutIDs = [];
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 15 minutes in milliseconds
const COUNTDOWN_SECONDS = 30; // 30 seconds before logout

const AuthContext = React.createContext({
  send_to: "",
  correlation_key: "",
  token: "",
  isLoggedIn: false,
  username: "",
  group: "",
  userId: null,
  login: () => {},
  verifyOTP: () => {},
  logout: () => {},
  refresh: () => {},
});

const checkRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();

  if (!expirationTime) {
    return -1;
  }

  // Convert expirationTime to timestamp
  let expTime;

  if (typeof expirationTime === 'string') {
    // If it's an ISO string, parse it as Date
    expTime = new Date(expirationTime).getTime();
  } else {
    // If it's already a number, use it directly
    expTime = parseInt(expirationTime);

    // If expiration time is in seconds (typical for JWT), convert to milliseconds
    if (expTime < 1e12) { // If less than 1 trillion, likely in seconds
      expTime = expTime * 1000;
    }
  }

  const remaining = expTime - currentTime;
  return remaining;
};

const retrieveStoredToken = () => {
  //Get token from session storage
  const storedToken = sessionStorage.getItem("access");
  const storedExpirationTime = sessionStorage.getItem("access_expiration_time");
  const username = sessionStorage.getItem("username");
  const group = sessionStorage.getItem("group");

  //Check how much time is left from the stored session
  const remainingTime = checkRemainingTime(storedExpirationTime);

  if (remainingTime <= 0) {
    //If time is over remove all the session variables and return null
    sessionStorage.removeItem("access");
    sessionStorage.removeItem("refresh");
    sessionStorage.removeItem("access_expiration_time");
    sessionStorage.removeItem("refresh_expiration_time");
    sessionStorage.removeItem("access_time");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("group");
    sessionStorage.removeItem("user_id");
    return null;
  }

  const userIdRaw = sessionStorage.getItem("user_id");

  //Otherwise return the session information
  return {
    token: storedToken,
    remainingTime: remainingTime,
    username: username,
    group: group,
    userId: userIdRaw != null && userIdRaw !== "" ? userIdRaw : null,
  };
};

// Separate component that manages its own state for the countdown.
// This prevents countdown ticks from re-rendering the entire context tree.
const InactivityManager = ({ isLoggedIn, onLogout }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(INACTIVITY_TIMEOUT / 1000);

  const inactivityTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const showWarningRef = useRef(false);

  // Keep ref in sync with state so event handlers see the latest value
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  const clearTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startInactivityTimer = useCallback(() => {
    // Prevent multiple intervals
    if (countdownIntervalRef.current) {
      return;
    }
    // Clear existing inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    let secondsLeft = INACTIVITY_TIMEOUT / 1000;
    setCountdown(secondsLeft);

    countdownIntervalRef.current = setInterval(() => {
      secondsLeft--;
      setCountdown(secondsLeft);
      if (secondsLeft <= COUNTDOWN_SECONDS) {
        setShowWarning(true);
      }
      if (secondsLeft <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        setShowWarning(false);
        onLogout();
      }
    }, 1000);

    inactivityTimerRef.current = setTimeout(() => {
      // Safety: clear interval if timer fires
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setShowWarning(false);
      onLogout();
    }, INACTIVITY_TIMEOUT);
  }, [onLogout]);

  const resetInactivityTimer = useCallback(() => {
    // Only reset if warning is NOT showing
    if (!showWarningRef.current) {
      clearTimers();
      setCountdown(INACTIVITY_TIMEOUT / 1000);
      startInactivityTimer();
    }
    // If warning is showing, do not reset on activity
  }, [clearTimers, startInactivityTimer]);

  const handleExtendSession = useCallback(() => {
    clearTimers();
    setCountdown(INACTIVITY_TIMEOUT / 1000);
    setShowWarning(false);
    startInactivityTimer();
  }, [clearTimers, startInactivityTimer]);

  // Start/stop inactivity timer based on login state
  useEffect(() => {
    if (isLoggedIn) {
      startInactivityTimer();
    } else {
      clearTimers();
      setShowWarning(false);
    }
    return () => {
      clearTimers();
    };
  }, [isLoggedIn, startInactivityTimer, clearTimers]);

  // Add/remove activity event listeners based on login and warning state
  useEffect(() => {
    if (isLoggedIn && !showWarning) {
      document.addEventListener("mousemove", resetInactivityTimer);
      document.addEventListener("mousedown", resetInactivityTimer);
      document.addEventListener("keypress", resetInactivityTimer);
      document.addEventListener("scroll", resetInactivityTimer);
      document.addEventListener("click", resetInactivityTimer);
    } else {
      document.removeEventListener("mousemove", resetInactivityTimer);
      document.removeEventListener("mousedown", resetInactivityTimer);
      document.removeEventListener("keypress", resetInactivityTimer);
      document.removeEventListener("scroll", resetInactivityTimer);
      document.removeEventListener("click", resetInactivityTimer);
    }

    // Cleanup function to always remove listeners
    return () => {
      document.removeEventListener("mousemove", resetInactivityTimer);
      document.removeEventListener("mousedown", resetInactivityTimer);
      document.removeEventListener("keypress", resetInactivityTimer);
      document.removeEventListener("scroll", resetInactivityTimer);
      document.removeEventListener("click", resetInactivityTimer);
    };
  }, [isLoggedIn, showWarning, resetInactivityTimer]);

  if (!showWarning) return null;

  return (
    <InactivityWarning
      isVisible={showWarning}
      countdown={countdown}
      onExtendSession={handleExtendSession}
      onLogoutNow={onLogout}
    />
  );
};

InactivityManager.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  onLogout: PropTypes.func.isRequired,
};


export const AuthContextProvider = (props) => {
  // const location = useLocation();

  const tokenData = retrieveStoredToken();

  let initalToken, initialUsername, initalGroup, initialUserId;
  if (tokenData) {
    initalToken = tokenData.token;
    initialUsername = tokenData.username;
    initalGroup = tokenData.group;
    initialUserId = tokenData.userId;
  }
  const [token, setToken] = useState(initalToken);
  const [username, setUsername] = useState(initialUsername);
  const [group, setGroup] = useState(initalGroup);
  const [userId, setUserId] = useState(initialUserId ?? null);

  const [send_to, setSend_To] = useState("");
  const [correlation_key, setCorrelation_Key] = useState("");
  const [messsage, setMessage] = useState("");

  const userIsLoggedIn = token ? true : false;

  // Define clearAllTimers first as it's used by other functions
  const clearAllTimers = () => {
    while (timeoutIDs.length > 0) {
      timeoutIDs.forEach((t) => {
        clearTimeout(t);
        timeoutIDs.splice(timeoutIDs.indexOf(t));
      });
    }
  };

  const logoutHandler = useCallback(() => {
    //Remove token
    setToken(null);
    setUserId(null);

    //Remove session variables
    sessionStorage.removeItem("access");
    sessionStorage.removeItem("access_expiration_time");
    sessionStorage.removeItem("refresh");
    sessionStorage.removeItem("refresh_expiration_time");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("group");
    sessionStorage.removeItem("user_id");

    if (timeoutIDs.length > 0) {
      //If the timer still exists clear it out.
      while (timeoutIDs.length > 0) {
        timeoutIDs.forEach((t) => {
          clearTimeout(t);
          timeoutIDs.splice(timeoutIDs.indexOf(t));
        });
      }
    }
  }, []);

  const loginHandler = (correlation_key, sent_to, message) => {
    //Set correlation key
    setCorrelation_Key(correlation_key);
    //Set email or contact number being sent to
    setSend_To(sent_to);
    //Set login response message
    setMessage(message);
  };

  const verifyOTPHandler = (
    token,
    expirationTime,
    refreshToken,
    refreshExpirationTime,
    accessTime,
    username,
    group,
    userPkOrId
  ) => {
    setToken(token);
    setUsername(username);
    setGroup(group);

    const resolvedUserId =
      userPkOrId !== undefined && userPkOrId !== null && userPkOrId !== ""
        ? String(userPkOrId)
        : null;
    setUserId(resolvedUserId);

    //Set session items
    sessionStorage.setItem("access", token);
    sessionStorage.setItem("access_expiration_time", expirationTime);
    sessionStorage.setItem("refresh", refreshToken);
    sessionStorage.setItem("refresh_expiration_time", refreshExpirationTime);
    sessionStorage.setItem("access_time", accessTime);
    sessionStorage.setItem("username", username);
    sessionStorage.setItem("group", group);
    if (resolvedUserId) {
      sessionStorage.setItem("user_id", resolvedUserId);
    } else {
      sessionStorage.removeItem("user_id");
    }

    //Get remaining session time
    const remainingTime = checkRemainingTime(expirationTime);

    if (remainingTime <= 0) {
      logoutHandler();
      return;
    }

    //Clear all timers
    clearAllTimers();
    //Set logout timer based on session time

    logoutTimer = setTimeout(logoutHandler, remainingTime);
    timeoutIDs.push(logoutTimer);
  };

  const refreshTokenHandler = (
    token,
    expirationTime,
    newRefreshToken,
    refreshExpirationTime
  ) => {
    //Set new access token
    setToken(token);

    //Re-set session variables
    sessionStorage.setItem("access", token);
    sessionStorage.setItem("access_expiration_time", expirationTime);
    sessionStorage.setItem("refresh", newRefreshToken);
    sessionStorage.setItem("refresh_expiration_time", refreshExpirationTime);

    //Get remaining time
    const remainingTime = checkRemainingTime(refreshExpirationTime);

    //Clear previous logout timer
    clearAllTimers();
    //Set new logout timer
    logoutTimer = setTimeout(logoutHandler, remainingTime);
    timeoutIDs.push(logoutTimer);
  };

  useEffect(() => {
    if (tokenData?.token) {
      clearAllTimers();
      // On load of the context check if there is still a token if the token is still there check how much time is left
      logoutTimer = setTimeout(logoutHandler, tokenData.remainingTime);
      timeoutIDs.push(logoutTimer);
    }
  }, [tokenData, logoutHandler]);

  const contextValue = {
    send_to: send_to,
    correlation_key: correlation_key,
    token: token,
    isLoggedIn: userIsLoggedIn,
    username: username,
    group: group,
    userId: userId,
    login: loginHandler,
    verifyOTP: verifyOTPHandler,
    logout: logoutHandler,
    refresh: refreshTokenHandler,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <InactivityManager isLoggedIn={userIsLoggedIn} onLogout={logoutHandler} />
      {props.children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.node,
};

export default AuthContext;
